/**
 * Copy protection on tech-card data (owner, 2026-10-12).
 *
 * The CSS in index.html makes card text unselectable; this guard covers what
 * CSS does not — Ctrl/Cmd+A then copy, a drag of text out of a card, and
 * browsers that still serialise unselectable text. Protected roots:
 *   .utech-nocopy   — opt-in class on the legacy cards, saved / For You cards,
 *                     researcher cards, chat cards, homepage teasers, the modal
 *   .tcx.tcx-card   — the accordion tech card (search results and the modal)
 *   .tpg-page       — the public /tech and university pages
 *
 * Left alone on purpose:
 *   - form fields (input, textarea, select, contenteditable): typing, copying
 *     and pasting stay normal, inside a card or not;
 *   - the right-click menu (links keep "open in new tab");
 *   - navigator.clipboard writes (Copy link, contact details, codes), which
 *     fire no copy event, and the share menu's fallback textarea, which is a
 *     form field outside the cards;
 *   - all of /admin.
 *
 * Switch: tech_pages setting `copy_protect` (Admin → Tech URL pages → Sections
 * & access), read once from GET /api/v1/ui-config. Off — or on /admin — the
 * guard stands down and html gets `utech-copy-ok`, which turns the CSS off.
 *
 * Who can still copy (owner, 2026-10-13; setting `copy_allow`): while the
 * switch is on, the signed-in account is asked about once, with its own
 * bearer (GET /api/v1/ui/copy-permission). An allowed account — admins,
 * listed user tags, addresses or @domains — gets the same stand-down. The
 * session is the stored one, so the public pages (outside App) are covered
 * too; App calls setCopyViewer() on sign-in and sign-out to ask afresh.
 * Signed out, or until the answer is in, the protection holds.
 *
 * A deterrent only: DevTools, view-source, print and screenshots still work.
 */
import { apiService } from '../services/apiService';

const ROOTS = '.utech-nocopy, .tcx.tcx-card, .tpg-page';
const FIELDS = 'input, textarea, select';
const OK_CLASS = 'utech-copy-ok';

let installed = false;
/** The admin's switch; on until the server says otherwise. */
let switchedOn = true;
let switchRequested = false;
/** The switch read has finished (either way), so the account can be asked. */
let switchLoaded = false;
/** The account the server said may copy, and the one last asked about. */
let allowedFor: string | null = null;
let askedFor: string | null = null;
/** Bumped on every viewer change, so a late answer for someone else is dropped. */
let askSeq = 0;
/** What App last said (setCopyViewer); undefined until it says anything. */
let appViewer: string | null | undefined;
/** After a failed ask (network, a restart's 502, an expired token), when it may
 *  be asked again. Only a real answer is kept, so a blip never latches. */
let retryAt = 0;
const RETRY_MS = 30_000;

const onAdmin = (): boolean => {
  try { return window.location.pathname.startsWith('/admin'); } catch { return false; }
};
/** The signed-in account: the stored session, whose bearer the ask carries. */
const viewer = (): string | null => {
  try { return apiService.getStoredUserId() || null; } catch { return null; }
};
const permitted = (): boolean => {
  const v = viewer();
  return !!v && allowedFor === v;
};
const active = (): boolean => switchedOn && !onAdmin() && !permitted();

/** One read of the switch, outside /admin; a failed call keeps the protection on. */
const loadSwitch = () => {
  if (switchRequested || onAdmin()) return;
  switchRequested = true;
  fetch(`${apiService.getApiBaseUrl()}/api/v1/ui-config`,
    { headers: { Accept: 'application/json' }, mode: 'cors', credentials: 'omit' })
    .then(r => (r.ok ? r.json() : null))
    .then(b => {
      const cfg = b && (b.data && typeof b.data === 'object' ? b.data : b);
      if (cfg && cfg.copy_protect === false) switchedOn = false;
    })
    .catch(() => { /* keep it on */ })
    .finally(() => { switchLoaded = true; syncClass(); });
};

/** While the switch is on, ask once per signed-in account whether it may copy
 *  anyway. No session, a refusal or any failure: the protection holds. */
const askPermission = () => {
  if (!switchLoaded || !switchedOn || onAdmin()) return;
  const uid = viewer();
  let token: string | null = null;
  try { token = apiService.getStoredAccessToken(); } catch { token = null; }
  if (!uid || !token || askedFor === uid || Date.now() < retryAt) return;
  askedFor = uid;
  const seq = ++askSeq;
  const failed = () => {
    if (seq !== askSeq) return;
    askedFor = null;
    retryAt = Date.now() + RETRY_MS;
  };
  apiService.getCopyPermission(uid)
    .then(res => {
      if (seq !== askSeq) return;
      if (!res.success) { failed(); return; }      // keep it on; ask again later
      if (res.data?.allowed === true) allowedFor = uid;
      syncClass();
    })
    .catch(failed);
};

/** html.utech-copy-ok whenever the guard is off, so the CSS follows it. */
function syncClass() {
  try { document.documentElement.classList.toggle(OK_CLASS, !active()); } catch { /* no DOM */ }
  loadSwitch();
  askPermission();
}

/** App: who has just signed in (null: signed out, or App has closed). Any
 *  change but the account already asked about on this load drops the earlier
 *  answer: the protection is back until the stored session, if any, has been
 *  asked about again. */
export function setCopyViewer(userId: string | null): void {
  const next = userId || null;
  if (next === appViewer) return;
  appViewer = next;
  if (next === null || next !== askedFor) {
    allowedFor = null;
    askedFor = null;
    askSeq++;
  }
  retryAt = 0;      // App has the session now: ask straight away
  syncClass();
}

/** The element a node or event target belongs to (a text node → its parent). */
const elementOf = (n: EventTarget | Node | null | undefined): Element | null => {
  if (!n) return null;
  if (n instanceof Element) return n;
  return n instanceof Node ? n.parentElement : null;
};

const isField = (el: Element | null): boolean => {
  if (!el) return false;
  if (el.closest(FIELDS)) return true;
  return !!(el as HTMLElement).isContentEditable;
};

/** Does any part of the current selection touch a protected root? */
const selectionTouchesRoot = (sel: Selection): boolean => {
  for (const n of [sel.anchorNode, sel.focusNode]) {
    if (elementOf(n)?.closest(ROOTS)) return true;
  }
  const roots = document.querySelectorAll(ROOTS);
  if (!roots.length) return false;
  for (let i = 0; i < sel.rangeCount; i++) {
    const r = sel.getRangeAt(i);
    for (let j = 0; j < roots.length; j++) {
      try { if (r.intersectsNode(roots[j])) return true; } catch { /* detached node */ }
    }
  }
  return false;
};

const onCopy = (e: ClipboardEvent) => {
  if (!active()) return;
  if (isField(elementOf(e.target)) || isField(document.activeElement)) return;
  const sel = window.getSelection();
  if (!sel || !sel.rangeCount || sel.isCollapsed) return;
  if (!selectionTouchesRoot(sel)) return;
  e.preventDefault();
  try { e.clipboardData?.setData('text/plain', ''); } catch { /* read-only clipboard */ }
};

/** selectstart / dragstart that begin inside a card, not in a form field. */
const onStart = (e: Event) => {
  if (!active()) return;
  const el = elementOf(e.target);
  if (!el || isField(el)) return;
  if (el.closest(ROOTS)) e.preventDefault();
};

export function installCopyGuard(): void {
  if (installed || typeof document === 'undefined') return;
  installed = true;
  syncClass();
  document.addEventListener('copy', onCopy, true);
  document.addEventListener('cut', onCopy, true);
  document.addEventListener('selectstart', onStart, true);
  document.addEventListener('dragstart', onStart, true);
  // The app moves between /admin and the rest without a reload: re-check the
  // path before any selection can start, and on back / forward.
  document.addEventListener('pointerdown', syncClass, true);
  window.addEventListener('popstate', syncClass);
}
